import { useState, useEffect, useRef, CSSProperties } from "react";

/**
 * Hook to help with writing tab bars with sliding glider effect.
 *
 * @param initialSelected
 * @param onTabChange
 * @returns object with following props:
 * activeTab - the current tab
 * gliderStyle - spread into the style of the glider element (style={gliderStyle})
 * getTabProps - function to get props for tab 'i'; splat the returned value on the tab component: <div {...getTabProps(i)}/>
 */
export const useTabBar = (initialSelected = 0, onTabChange?: (i: number) => void) => {
  const [activeTab, setActiveTab] = useState(initialSelected);
  const [gliderStyle, setGliderStyle] = useState<CSSProperties>({});
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    onTabChange?.(index);
  };

  const registerTab = (index: number) => (el: HTMLButtonElement | null) => {
    tabRefs.current[index] = el;
  };

  useEffect(() => {
    const activeElement = tabRefs.current[activeTab];
    if (activeElement) {
      setGliderStyle({
        width: activeElement.offsetWidth,
        left: activeElement.offsetLeft,
      });
    }
  }, [activeTab]);

  return {
    activeTab,
    gliderStyle,
    getTabProps: (index: number) => ({
      ref: registerTab(index),
      onClick: () => handleTabClick(index),
      "data-active": activeTab === index,
    }),
  } as const;
};
